/**
 * React Query configuration constants
 */

// Time constants (in milliseconds)
const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;

export const TIME = {
  ONE_SECOND_MS: MILLISECONDS_PER_SECOND,
  ONE_MINUTE_MS: SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND,
  FIVE_MINUTES_MS: 5 * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND,
  FIFTEEN_MINUTES_MS: 15 * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND,
  TWENTY_MINUTES_MS: 20 * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND,
  TWENTY_NINE_MINUTES_MS: 29 * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND,
  THIRTY_MINUTES_MS: 30 * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND,
  TWO_MINUTES_MS: 2 * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND,
  TWO_HOURS_MS: 2 * MINUTES_PER_HOUR * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND,
} as const;

/**
 * Default stale time for React Query queries (5 minutes)
 */
export const DEFAULT_QUERY_STALE_TIME = TIME.FIVE_MINUTES_MS;

/**
 * Default retry count for React Query queries
 */
export const DEFAULT_QUERY_RETRY_COUNT = 1;

/**
 * Standard retry count for critical queries
 */
export const CRITICAL_QUERY_RETRY_COUNT = 2;

/**
 * Maximum retry delay for exponential backoff (30 seconds)
 */
export const MAX_RETRY_DELAY_MS = 30 * TIME.ONE_SECOND_MS;

/**
 * Base delay for exponential backoff retry strategy (1 second)
 */
export const BASE_RETRY_DELAY_MS = TIME.ONE_SECOND_MS;

/**
 * ThoughtSpot SDK Token configuration
 */
export const SDK_TOKEN = {
  /**
   * How long the SDK token is considered fresh (29 minutes)
   * Set to 29 minutes to provide 1 minute buffer before 30-minute expiry
   */
  STALE_TIME_MS: TIME.TWENTY_NINE_MINUTES_MS,
  /**
   * How often to refetch the SDK token in the background (20 minutes)
   * Provides 10-minute buffer before token expiry
   */
  REFETCH_INTERVAL_MS: TIME.TWENTY_MINUTES_MS,
  /**
   * Actual token lifetime from the server (30 minutes)
   */
  LIFETIME_MS: TIME.THIRTY_MINUTES_MS,
  /**
   * Safety margin before token expiry to trigger refresh (2 minutes)
   */
  REFRESH_THRESHOLD_MS: TIME.TWO_MINUTES_MS,
} as const;

/**
 * Impersonation query configuration
 */
export const IMPERSONATION = {
  /**
   * How long impersonation status/access data is considered fresh (30 minutes)
   */
  STALE_TIME_MS: TIME.THIRTY_MINUTES_MS,
} as const;

/**
 * ThoughtSpot maintenance banner configuration
 */
export const MAINTENANCE_BANNER = {
  /**
   * How often to check for maintenance banner updates (15 minutes)
   */
  REFETCH_INTERVAL_MS: TIME.FIFTEEN_MINUTES_MS,
} as const;

/**
 * Async job polling configuration
 */
export const JOB_POLLING = {
  INTERVAL_MS: 3 * TIME.ONE_SECOND_MS,
  MAX_DURATION_MS: 60 * TIME.ONE_SECOND_MS,
} as const;
